import React, { useState } from 'react';
import { useParams, Link, useOutletContext } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { 
  Globe, 
  MessageSquare, 
  ShieldCheck, 
  Star, 
  Download, 
  Heart, 
  Layers, 
  MapPin, 
  Calendar, 
  CheckCircle2, 
  ExternalLink,
  Sparkles,
  Share2,
  Briefcase,
  Award
} from 'lucide-react';
import { 
  BehanceIcon, 
  DribbbleIcon, 
  InstagramIcon, 
  TwitterXIcon, 
  LinkedInIcon 
} from '../components/SocialIcons';
import { fetchDesigner, fetchDesignerTemplates } from '../api/client';
import { useAuth } from '../context/AuthContext';
import TemplateCard from '../components/TemplateCard';
import SkeletonCard from '../components/SkeletonCard';
import Button from '../components/Button';
import ChatModal from '../components/ChatModal';
import TrustScoreGauge from '../components/TrustScoreGauge';
import ShareModal from '../components/ShareModal';
import './DesignerProfile.css';

export default function DesignerProfile() {
  const { username } = useParams();
  const { user } = useAuth();
  const { setAuthOpen } = useOutletContext() || {};
  const [activeTab, setActiveTab] = useState('portfolio'); // 'portfolio' | 'trust' | 'about'
  const [chatOpen, setChatOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);

  const { data: profileData, isLoading: profileLoading, isError: profileError } = useQuery({
    queryKey: ['designer', username],
    queryFn: () => fetchDesigner(username),
  });

  const { data: templatesData, isLoading: templatesLoading } = useQuery({
    queryKey: ['designerTemplates', username],
    queryFn: () => fetchDesignerTemplates(username),
  });

  const designer = profileData?.designer;
  const templates = templatesData?.templates || [];

  if (profileLoading) {
    return (
      <div className="page-container" style={{ padding: '80px 0', textAlign: 'center' }}>
        <div className="spinner" />
        <p style={{ color: 'var(--text-secondary)' }}>Loading designer showcase...</p>
      </div>
    );
  }

  if (profileError || !designer) {
    return (
      <div className="page-container" style={{ padding: '80px 0', textAlign: 'center' }}>
        <h2 className="text-h2" style={{ marginBottom: '16px' }}>Designer Not Found</h2>
        <p style={{ color: 'var(--text-secondary)', marginBottom: '24px' }}>
          The creative profile @{username} doesn't exist or is currently private.
        </p>
        <Link to="/browse">
          <Button variant="primary">Explore Templates</Button>
        </Link>
      </div>
    );
  }

  const socials = designer.social_links || {};
  const skills = Array.isArray(designer.skills) ? designer.skills : [];

  const handleOpenChat = () => {
    if (!user) {
      if (setAuthOpen) setAuthOpen(true);
      return;
    }
    setChatOpen(true);
  };

  return (
    <div className="designer-profile-page page-container" style={{ paddingBottom: '100px' }}>
      {/* ── Hero Backdrop Banner ── */}
      <div className="profile-banner-hero glass-panel">
        <div className="banner-gradient-backdrop" />
        
        <div className="profile-banner-content">
          <div className="profile-avatar-stack">
            <img 
              src={designer.avatar_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${designer.display_name}`} 
              alt={designer.display_name} 
              className="profile-hero-avatar"
            />
            {designer.ghana_card_verified === 1 && (
              <div className="profile-verified-chip" title="Verified Creator">
                <ShieldCheck size={16} />
              </div>
            )}
          </div>

          <div className="profile-info-block">
            <div className="profile-name-row">
              <h1 className="profile-display-name">
                {designer.first_name && designer.last_name 
                  ? `${designer.first_name} ${designer.last_name}`
                  : designer.display_name}
              </h1>
              <span className="profile-handle">@{designer.display_name}</span>
            </div>

            <div className="profile-specialization-badge">
              <Sparkles size={14} className="text-violet" />
              <span>{designer.specialization || 'Master Visual & UI/UX Designer'}</span>
            </div>

            {designer.bio && (
              <p className="profile-bio-text">{designer.bio}</p>
            )}

            {/* Meta details & stats */}
            <div className="profile-meta-chips-row">
              <div className="meta-chip">
                <MapPin size={14} />
                <span>{designer.location || designer.nationality || 'Accra, Ghana'}</span>
              </div>
              <div className="meta-chip">
                <Star size={14} className="text-warning" />
                <span>{designer.trust_score ? Number(designer.trust_score).toFixed(1) : '4.9'} Rating</span>
              </div>
              <div className="meta-chip">
                <Download size={14} />
                <span>{(designer.total_downloads || 0).toLocaleString()} Downloads</span>
              </div>
              <div className="meta-chip">
                <Layers size={14} />
                <span>{templates.length} Templates</span>
              </div>
            </div>

            {/* Social Links Bar */}
            <div className="profile-socials-bar">
              {socials.website && (
                <a href={socials.website} target="_blank" rel="noopener noreferrer" className="social-icon-btn" title="Portfolio Website">
                  <Globe size={18} />
                </a>
              )}
              {socials.behance && (
                <a href={socials.behance} target="_blank" rel="noopener noreferrer" className="social-icon-btn" title="Behance Case Studies">
                  <BehanceIcon size={18} />
                </a>
              )}
              {socials.dribbble && (
                <a href={socials.dribbble} target="_blank" rel="noopener noreferrer" className="social-icon-btn" title="Dribbble Showcase">
                  <DribbbleIcon size={18} />
                </a>
              )}
              {socials.instagram && (
                <a href={socials.instagram} target="_blank" rel="noopener noreferrer" className="social-icon-btn" title="Instagram">
                  <InstagramIcon size={18} />
                </a>
              )}
              {socials.twitter && (
                <a href={socials.twitter} target="_blank" rel="noopener noreferrer" className="social-icon-btn" title="Twitter / X">
                  <TwitterXIcon size={16} />
                </a>
              )}
              {socials.linkedin && (
                <a href={socials.linkedin} target="_blank" rel="noopener noreferrer" className="social-icon-btn" title="LinkedIn">
                  <LinkedInIcon size={18} />
                </a>
              )}
            </div>
          </div>

          {/* Action CTAs */}
          <div className="profile-action-column">
            <Button 
              variant="primary" 
              size="lg" 
              onClick={handleOpenChat}
              className="chat-designer-cta-btn"
            >
              <MessageSquare size={18} />
              <span>Message Designer</span>
            </Button>

            <button className="share-profile-btn tactile-btn" onClick={() => setShareModalOpen(true)}>
              <Share2 size={16} />
              <span>Share Profile</span>
            </button>
          </div>
        </div>
      </div>

      {/* ── Sub Navigation Tabs ── */}
      <div className="profile-nav-tabs" style={{ marginBottom: '28px' }}>
        <button 
          className={`profile-tab-btn ${activeTab === 'portfolio' ? 'active' : ''}`}
          onClick={() => setActiveTab('portfolio')}
        >
          <Layers size={16} />
          <span>Templates & Assets ({templates.length})</span>
        </button>

        <button 
          className={`profile-tab-btn ${activeTab === 'trust' ? 'active' : ''}`}
          onClick={() => setActiveTab('trust')}
        >
          <Award size={16} />
          <span>Trust & Credibility Score</span>
        </button>

        <button 
          className={`profile-tab-btn ${activeTab === 'about' ? 'active' : ''}`}
          onClick={() => setActiveTab('about')}
        >
          <Briefcase size={16} />
          <span>About & Skills</span>
        </button>
      </div>

      {/* ── TAB 1: Portfolio Grid ── */}
      {activeTab === 'portfolio' && (
        <section className="profile-portfolio-section">
          {templatesLoading ? (
            <div className="template-grid">
              {Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} />)}
            </div>
          ) : templates.length > 0 ? (
            <div className="template-grid">
              {templates.map(t => (
                <TemplateCard 
                  key={t.id} 
                  id={t.id}
                  title={t.title}
                  slug={t.slug}
                  designer={designer.display_name}
                  likes={t.like_count}
                  rating={t.avg_rating}
                  downloads={t.download_count}
                  price={t.price_cents ? t.price_cents / 100 : 0}
                  formats={['PSD', 'AI', 'FIG']} 
                  image={t.preview_url}
                />
              ))}
            </div>
          ) : (
            <div className="empty-portfolio-card glass-panel">
              <Layers size={40} className="text-violet" />
              <h3>No templates published yet</h3>
              <p>@{designer.display_name} is currently crafting new assets for the marketplace.</p>
            </div>
          )}
        </section>
      )}

      {/* ── TAB 2: Trust & Reputation Gauge ── */}
      {activeTab === 'trust' && (
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <TrustScoreGauge
            score={98}
            rank="Elite Top Creator"
            stats={{
              deliveryRate: "99.8%",
              avgResponse: "< 1 hr",
              positiveReviews: "99.2%",
              totalSales: `${(designer.total_downloads || 340).toLocaleString()}+`
            }}
          />
        </div>
      )}

      {/* ── TAB 3: About & Skills ── */}
      {activeTab === 'about' && (
        <div className="profile-about-grid">
          <div className="about-card glass-panel">
            <h3>Creative Biography</h3>
            <p className="about-bio-full">
              {designer.bio || `${designer.display_name} is a seasoned creative designer specializing in visual design, marketing assets, and design systems on Buildive.`}
            </p>

            <h4 style={{ marginTop: '24px', marginBottom: '12px' }}>Specialized Skills & Disciplines</h4>
            <div className="skills-tags-row">
              {(skills.length > 0 ? skills : ['Photoshop', 'Illustrator', 'Figma', 'Poster Design', 'Branding', 'Typography', 'Visual Arts']).map(skill => (
                <span key={skill} className="skill-chip">
                  <CheckCircle2 size={13} className="text-green" />
                  <span>{skill}</span>
                </span>
              ))}
            </div>
          </div>

          <div className="about-sidebar glass-panel">
            <h3>Creator Verification</h3>
            <div className="verification-item">
              <ShieldCheck size={20} className="text-green" />
              <div>
                <strong>Identity Verified Pro</strong>
                <p>Official verified creator status authenticated on Buildive protocol.</p>
              </div>
            </div>

            <div className="verification-item" style={{ marginTop: '16px' }}>
              <Star size={20} className="text-warning" />
              <div>
                <strong>High Trust Creator</strong>
                <p>Consistently high buyer ratings and zero copyright infractions.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      <ChatModal 
        recipient={designer} 
        isOpen={chatOpen} 
        onClose={() => setChatOpen(false)} 
      />

      <ShareModal
        isOpen={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        title={`Share @${designer.display_name}'s Profile`}
      />
    </div>
  );
}
